import React, { useState } from 'react';
import { TextField, Button, Paper, Typography, Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth, db } from '../firebase';
import { collection, addDoc, setDoc, doc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';

const VisitorQRRegistration = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const tenantId = searchParams.get('tenantId');

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
  });
  const [emailVerified, setEmailVerified] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (e.target.name === 'email') {
      setEmailVerified(false);
    }
  };

  const handleGoogleVerify = async () => {
    setError('');
    setSuccess('');
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const email = result.user.email;
      if (email && email === form.email) {
        setEmailVerified(true);
        setSuccess('Email verified with Google!');
      } else {
        setError('The Google account does not match the entered email.');
        setEmailVerified(false);
      }
    } catch (err) {
      setError('Google authentication failed. ' + err.message);
      setEmailVerified(false);
    }
  };

  const validateInputs = () => {
    // Phone: only numbers, length 10-12
    if (!/^[0-9]{10,12}$/.test(form.phone)) {
      setError('Phone number must be 10-12 digits.');
      return false;
    }
    // Email: must contain @ and .
    if (!/^.+@.+\..+$/.test(form.email)) {
      setError('Please enter a valid email address.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!validateInputs()) return;
    if (!emailVerified) {
      setError('Please verify your email with Google before registering.');
      return;
    }
    setLoading(true);
    try {
      // Check if email already exists in Firestore users collection
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('userEmailId', '==', form.email));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        setError('This email is already registered in the system. Please log in instead.');
        setLoading(false);
        return;
      }
      // Add user to Firestore users collection (no password)
      const userId = `${form.email.replace(/[^a-zA-Z0-9]/g, '')}_${Date.now()}`;
      await setDoc(doc(db, 'users', userId), {
        userId: userId,
        userName: form.name,
        userEmailId: form.email,
        userMobileNo: form.phone,
        role: 'visitor',
        tenantId: tenantId || '',
        createdAt: serverTimestamp(),
        registeredViaQR: true,
      });
      setSuccess('Registration successful! You can now log in.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const now = new Date();
  const dateTimeString = now.toLocaleString();

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f5f5f5">
      <Paper elevation={3} sx={{ p: 4, minWidth: 350 }}>
        <Typography variant="h5" gutterBottom>Visitor Registration</Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Date & Time: {dateTimeString}
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            label="Name"
            name="name"
            value={form.name}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Phone Number"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Email ID"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            InputProps={{
              endAdornment: (
                <Button
                  variant={emailVerified ? 'contained' : 'outlined'}
                  color={emailVerified ? 'success' : 'primary'}
                  size="small"
                  onClick={handleGoogleVerify}
                  disabled={!form.email || emailVerified}
                  sx={{ ml: 1 }}
                >
                  {emailVerified ? 'Verified' : 'Verify'}
                </Button>
              ),
            }}
          />
          {error && <>
            <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>
            {error.includes('already registered') && (
              <Button sx={{ mt: 2 }} variant="outlined" color="primary" onClick={() => navigate(`/login?tenantId=${tenantId || ''}`)}>
                Go to Login
              </Button>
            )}
          </>}
          {success && <Typography color="success.main" sx={{ mt: 1 }}>{success}</Typography>}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            disabled={loading}
          >
            {loading ? 'Registering...' : 'Register'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default VisitorQRRegistration;